import React, { useState, useEffect } from 'react';
import { Appointment, AppointmentStatus } from '../../types/appointment.types';
import { Barber, DayOfWeek } from '../../types/barber.types';

interface DailyCalendarProps {
    appointments: Appointment[];
    barber?: Barber | null;
    onAppointmentClick?: (appointment: Appointment) => void;
    onSlotClick?: (date: Date, time: string) => void;
    onDayChange?: (day: Date) => void;
    initialDay?: Date | null;
}

const DailyCalendar: React.FC<DailyCalendarProps> = ({
    appointments,
    barber,
    onAppointmentClick,
    onSlotClick,
    onDayChange,
    initialDay
}) => {
    // In DailyCalendar, currentDay represents the currently viewed single day
    const [currentDay, setCurrentDay] = useState<Date>(initialDay ? new Date(initialDay) : new Date());
    const [currentTime, setCurrentTime] = useState<Date>(new Date());
    const currentTimeRef = React.useRef<HTMLDivElement>(null);

    // Intelligent scroll logic
    useEffect(() => {
        const timer = setTimeout(() => {
            const now = new Date();
            const isViewingToday = isToday(currentDay);
            const allHours = getHours();

            // Determinar inicio y fin de jornada
            let shiftStartStr = '09:15';
            let shiftEndStr = '22:00';
            
            if (barber && barber.schedules && barber.schedules.length > 0) {
                const dayEnum = getDayOfWeekEnum(currentDay);
                let schedule = barber.schedules.find(s => s.dayOfWeek === dayEnum && s.isAvailable);
                
                // Si no hay horario para hoy, usar el primero disponible
                if (!schedule) {
                    schedule = barber.schedules.find(s => s.isAvailable);
                }

                if (schedule) {
                    shiftStartStr = schedule.startTime.substring(0, 5);
                    shiftEndStr = schedule.endTime.substring(0, 5);
                }
            } else if (allHours.length > 0) {
                shiftStartStr = allHours[0];
                shiftEndStr = allHours[allHours.length - 1];
            }

            const scrollToTarget = (targetTimeStr: string) => {
                let closestHourStr = allHours[0];
                let minDiff = Infinity;
                
                const [th, tm] = targetTimeStr.split(':');
                const targetMins = parseInt(th, 10) * 60 + parseInt(tm, 10);

                for (const hStr of allHours) {
                    const [h, m] = hStr.split(':');
                    const hMins = parseInt(h, 10) * 60 + parseInt(m, 10);
                    const diff = Math.abs(hMins - targetMins);
                    if (diff < minDiff) {
                        minDiff = diff;
                        closestHourStr = hStr;
                    }
                }

                const el = document.getElementById(`cell-day-${closestHourStr}`);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'center' });
                }
            };

            if (isViewingToday) {
                const currentMinutes = now.getHours() * 60 + now.getMinutes();
                const [sh, sm] = shiftStartStr.split(':');
                const shiftStartMinutes = parseInt(sh, 10) * 60 + parseInt(sm, 10);
                
                const [eh, em] = shiftEndStr.split(':');
                const shiftEndMinutes = parseInt(eh, 10) * 60 + parseInt(em, 10) + 45;

                if (currentMinutes < shiftStartMinutes || currentMinutes >= shiftEndMinutes) {
                    // Fuera del rango de atencion -> posicionarse por defecto en la primer hora laboral
                    scrollToTarget(shiftStartStr);
                } else {
                    // Dentro del rango de atencion -> posicionarse en la hora actual
                    if (currentTimeRef.current) {
                        currentTimeRef.current.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'center' });
                    } else {
                        const currentHourStr = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
                        scrollToTarget(currentHourStr);
                    }
                }
            } else {
                // No estoy en el dia de hoy -> posicionarse por defecto en la primer hora laboral
                scrollToTarget(shiftStartStr);
            }
        }, 150);

        return () => clearTimeout(timer);
    }, [currentDay, appointments, barber]);

    // Update current time every minute
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(new Date());
        }, 60000);
        return () => clearInterval(interval);
    }, []);

    // Notify parent on initial load
    useEffect(() => {
        onDayChange?.(currentDay);
    }, []); 

    function formatDate(date: Date): string {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    function formatDayHeaderFull(date: Date): string {
        const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
        const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
        return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]}`;
    }

    function isToday(date: Date): boolean {
        const today = new Date();
        return date.toDateString() === today.toDateString();
    }

    function goToToday() {
        const newDay = new Date();
        setCurrentDay(newDay);
        onDayChange?.(newDay);
    }

    function goToPreviousDay() {
        const newDate = new Date(currentDay);
        newDate.setDate(currentDay.getDate() - 1);
        setCurrentDay(newDate);
        onDayChange?.(newDate);
    }

    function goToNextDay() {
        const newDate = new Date(currentDay);
        newDate.setDate(currentDay.getDate() + 1);
        setCurrentDay(newDate);
        onDayChange?.(newDate);
    }

    function getDayOfWeekEnum(date: Date): DayOfWeek {
        const days = [DayOfWeek.SUNDAY, DayOfWeek.MONDAY, DayOfWeek.TUESDAY, DayOfWeek.WEDNESDAY, DayOfWeek.THURSDAY, DayOfWeek.FRIDAY, DayOfWeek.SATURDAY];
        return days[date.getDay()];
    }

    const fixedBlocks = [
        '09:15', '10:00', '10:45', '11:30', '12:15', '13:00',
        '13:45', '14:30', '15:15', '16:00', '16:45', '17:30',
        '18:15', '19:00', '19:45', '20:30', '21:15', '22:00'
    ];

    function getHours(): string[] {
        const dateStr = formatDate(currentDay);
        const aptTimes = appointments
            .filter(apt => apt.appointmentDate === dateStr)
            .map(apt => apt.startTime.substring(0, 5));
        
        const allTimes = Array.from(new Set([...fixedBlocks, ...aptTimes]));
        return allTimes.sort();
    }

    function getAppointmentsForDayAndHour(date: Date, hour: string): Appointment[] {
        const dateStr = formatDate(date);
        return appointments.filter(apt => {
            if (apt.appointmentDate !== dateStr) return false;
            return apt.startTime.substring(0, 5) === hour;
        });
    }

    function getStatusColor(status: AppointmentStatus): string {
        const colors: Record<AppointmentStatus, string> = {
            PENDING: '#f59e0b',
            CONFIRMED: '#3b82f6',
            IN_PROGRESS: '#8b5cf6',
            COMPLETED: '#10b981',
            CANCELLED: '#ef4444',
            NO_SHOW: '#6b7280'
        };
        return colors[status] || '#6b7280';
    }

    const hours = getHours();
    const isCurrentDay = isToday(currentDay);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.75rem',
                backgroundColor: '#111111',
                borderRadius: '0.75rem',
                border: '1px solid #2a2a2a',
                gap: '0.5rem'
            }}>
                <button
                    onClick={goToPreviousDay}
                    style={{
                        padding: '0.5rem 0.75rem',
                        backgroundColor: '#1a1a1a',
                        border: '1px solid #333',
                        borderRadius: '0.5rem',
                        cursor: 'pointer',
                        color: '#a3a3a3',
                        fontWeight: '600'
                    }}
                >
                    ◀ Ayer
                </button>
                
                <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    flex: 1,
                    minWidth: '120px'
                }}>
                    <h2 style={{ 
                        margin: 0, 
                        fontSize: '1.1rem', 
                        fontWeight: '700', 
                        color: isCurrentDay ? '#f59e0b' : '#e5e5e5',
                        textAlign: 'center'
                    }}>
                        {formatDayHeaderFull(currentDay)}
                    </h2>
                    {!isCurrentDay && (
                        <button
                            onClick={goToToday}
                            style={{ 
                                marginTop: '0.25rem',
                                padding: '0.15rem 0.5rem', 
                                background: '#f59e0b20', 
                                border: '1px solid #f59e0b50', 
                                color: '#f59e0b', 
                                borderRadius: '0.25rem', 
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                cursor: 'pointer'
                            }}
                        >
                            Ir a Hoy
                        </button>
                    )}
                </div>

                <button
                    onClick={goToNextDay}
                    style={{
                        padding: '0.5rem 0.75rem',
                        backgroundColor: '#1a1a1a',
                        border: '1px solid #333',
                        borderRadius: '0.5rem',
                        cursor: 'pointer',
                        color: '#a3a3a3',
                        fontWeight: '600'
                    }}
                >
                    Mañ ▶
                </button>
            </div>

            {/* Calendar Grid Container */}
            <div 
                id="daily-scroll-container"
                style={{
                backgroundColor: '#111111',
                borderRadius: '0.75rem',
                border: '1px solid #2a2a2a',
                overflow: 'auto',
                maxHeight: '70vh',
                position: 'relative'
            }}>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '50px 1fr', // Narrower time column for mobile
                    position: 'relative',
                    width: '100%' // Ensure it fits the container instead of overflowing
                }}>
                    {/* Time Rows */}
                    {hours.map((hour, hourIdx) => {
                        const dayAppointments = getAppointmentsForDayAndHour(currentDay, hour);
                        return (
                        <React.Fragment key={hour}>
                            {/* Hour Label */}
                            <div style={{
                                borderTop: hourIdx > 0 ? '1px solid #2a2a2a' : 'none',
                                padding: '0.5rem 0.25rem',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                color: '#f59e0b',
                                textAlign: 'right',
                                backgroundColor: '#1a1a1a',
                                zIndex: 20
                            }}>
                                {hour}
                            </div>

                            {/* Day Cell */}
                            <div 
                                id={`cell-day-${hour}`}
                                style={{
                                borderTop: hourIdx > 0 ? '1px solid #2a2a2a' : 'none',
                                borderLeft: '1px solid #2a2a2a',
                                backgroundColor: isCurrentDay ? '#f59e0b08' : '#111111',
                                padding: '0.25rem',
                                minHeight: '80px', // slightly taller for better mobile tap targets
                                position: 'relative',
                                zIndex: 10,
                                scrollMarginTop: '70px'
                            }}>
                                {/* Single clickable 45-min block background */}
                                <div 
                                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1, cursor: 'pointer' }}
                                    onClick={() => onSlotClick?.(currentDay, hour)}
                                />

                                {/* Appointments Container (above slots) */}
                                <div style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                    {dayAppointments.map((apt) => (
                                        <div
                                            key={apt.id}
                                            onClick={() => onAppointmentClick?.(apt)}
                                        style={{
                                            background: `linear-gradient(135deg, ${getStatusColor(apt.status)}, ${getStatusColor(apt.status)}dd)`,
                                            borderLeft: `4px solid ${getStatusColor(apt.status)}`,
                                            padding: '0.5rem 0.6rem',
                                            marginBottom: '0.25rem',
                                            borderRadius: '0.5rem',
                                            fontSize: '0.8rem',
                                            cursor: onAppointmentClick ? 'pointer' : 'default',
                                            transition: 'all 0.2s',
                                            boxShadow: `0 2px 6px ${getStatusColor(apt.status)}40`
                                        }}
                                    >
                                        <div style={{
                                            fontWeight: '600',
                                            color: 'rgba(255,255,255,0.9)',
                                            marginBottom: '0.2rem',
                                            fontSize: '0.75rem'
                                        }}>
                                            {apt.startTime.substring(0, 5)} - {apt.endTime.substring(0, 5)}
                                        </div>
                                        <div style={{
                                            fontWeight: '700',
                                            color: '#fff',
                                            marginBottom: '0.2rem',
                                            fontSize: '0.95rem'
                                        }}>
                                            {apt.clientName}
                                        </div>
                                        <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.8rem' }}>
                                            {apt.serviceDescription || 'Servicio'}
                                        </div>
                                    </div>
                                ))}
                                </div>

                                {/* Current Time Indicator */}
                                {(() => {
                                    if (!isCurrentDay) return null;
                                    const [blockHourStr, blockMinuteStr] = hour.split(':');
                                    const blockTimeMinutes = parseInt(blockHourStr, 10) * 60 + parseInt(blockMinuteStr, 10);
                                    const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes();
                                    const isCurrentBlock = currentMinutes >= blockTimeMinutes && currentMinutes < blockTimeMinutes + 45;
                                    
                                    if (isCurrentBlock) {
                                        const percentage = ((currentMinutes - blockTimeMinutes) / 45) * 100;
                                        return (
                                            <div 
                                                ref={currentTimeRef}
                                                style={{
                                                position: 'absolute',
                                                top: `${percentage}%`,
                                                left: '-1px',
                                                right: 0,
                                                height: '2px',
                                                backgroundColor: '#ef4444',
                                                zIndex: 100,
                                                pointerEvents: 'none'
                                            }}>
                                                <div style={{
                                                    position: 'absolute',
                                                    left: '-4px',
                                                    top: '-3px',
                                                    width: '8px',
                                                    height: '8px',
                                                    borderRadius: '50%',
                                                    backgroundColor: '#ef4444'
                                                }} />
                                            </div>
                                        );
                                    }
                                    return null;
                                })()}
                            </div>
                        </React.Fragment>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default DailyCalendar;
